import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { invalidateBatch } from "./batch";
import { humanDate, money, personName, stamp } from "./format";
import { canEnd, endOverride, endRefusal, enderName, isOlderApi, overridesQuery, OVERRIDE_COPY, refreshOverrides, returnApplyText, statusText, type EndAnswer, type Override } from "./overrides";
import { refreshAfterCommit } from "./rrp";

const C = OVERRIDE_COPY.section;
const STATUS_CLASS: Record<Override["status"], string> = {
  in_force: "border-ks-purple text-ks-purple", due: "border-ks-warn text-ks-warn", returned: "border-ks-grey-2 text-ks-grey-1", replaced: "border-ks-grey-2 text-ks-grey-1",
};

/**
 * The Price overrides section of the Price Run tab (C1 v1.50, c1-overrides): C2 v1.52's `GET /api/overrides`, every override in
 * force or due and every one ended in the last 30 days, newest first, with End now on the ones a person may end. End now writes
 * the return under the person's name (C2 refuses a view-only person; the shell renders the button inert, AGENTS rule 11) and the
 * answer becomes the apply-by-hand line above the table, with Done to dismiss it; the price runs, the RRP overlay and the batch
 * reads are read again, since the return is a committed price. A refusal is an alert in C2's words (rule 14). Nothing is computed:
 * every price, date, count and days-left figure is C2's.
 */
export function OverridesSection({ areaName }: { areaName: string }) {
  const client = useQueryClient();
  const read = useQuery(overridesQuery());
  const [ended, setEnded] = useState<EndAnswer | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [ending, setEnding] = useState<number | null>(null);
  const end = useMutation({
    mutationFn: (entryId: number) => endOverride(entryId),
    onMutate: (entryId) => { setEnding(entryId); setRefusal(null); },
    onSuccess: (answer) => { setEnded(answer); refreshOverrides(client); refreshAfterCommit(client); invalidateBatch(client); },
    onError: (error) => { setRefusal(endRefusal(error, areaName)); refreshOverrides(client); },
    onSettled: () => setEnding(null),
  });
  const heading = (
    <div>
      <h3 className="text-sm font-semibold text-ks-ink">{C.title}</h3>
      <p className="text-xs text-ks-grey-1">{C.note}</p>
    </div>
  );
  const body = read.error && isOlderApi(read.error)
    ? <p role="status" className="text-sm text-ks-grey-1" data-overrides-older-api>{C.olderApi}</p>
    : read.error
    ? <p role="alert" className="text-sm">{C.unread}<Button size="sm" variant="outline" className="ml-2" data-nonmutating onClick={() => void read.refetch()}>Retry</Button></p>
    : !read.data
      ? <p role="status" className="text-xs text-ks-grey-1">reading the overrides</p>
      : read.data.overrides.length === 0
        ? <p className="text-sm text-ks-grey-1" data-overrides-empty>{C.empty}</p>
        : (
          <>
            <p className="text-xs text-ks-grey-1" data-overrides-counts>{C.counts(read.data.counts)}</p>
            <Table>
              <TableHeader>
                <TableRow>{C.columns.map((col, i) => <TableHead key={i} className={i === 2 ? "text-right" : undefined}>{col}</TableHead>)}</TableRow>
              </TableHeader>
              <TableBody>
                {read.data.overrides.map((o) => (
                  <TableRow key={o.entry_id} className={o.status === "returned" || o.status === "replaced" ? "text-ks-grey-1" : undefined} data-override={o.entry_id}>
                    <TableCell><span className="font-semibold text-ks-ink">{o.product} {o.variant}</span><div className="text-xs text-ks-grey-1">{o.xero_sku}</div></TableCell>
                    <TableCell>{o.marketplace_name ?? o.marketplace_id}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      <b>{money(o.price, o.currency)}</b>
                      <div className="text-xs text-ks-grey-1">{o.return_price === null ? C.noReturn : C.returnsTo(money(o.return_price, o.currency))}</div>
                    </TableCell>
                    <TableCell><span className="text-sm">{o.reason}</span><div className="text-xs text-ks-grey-1">{C.committedBy(personName(o.approver), stamp(o.committed_at))}</div></TableCell>
                    <TableCell className="whitespace-nowrap">{humanDate(o.until)}</TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_CLASS[o.status]}>{statusText(o)}</Badge></TableCell>
                    <TableCell>
                      {canEnd(o) && (
                        <Button type="button" size="sm" variant="outline" disabled={end.isPending} aria-busy={ending === o.entry_id} onClick={() => end.mutate(o.entry_id)} data-override-end={o.entry_id}>
                          {ending === o.entry_id ? <><Loader2 aria-hidden className="size-4 animate-spin" />{C.ending}</> : C.end}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        );
  return (
    <section role="region" aria-label={C.title} className="flex flex-col gap-2 rounded-md border border-ks-grey-2 p-3">
      {heading}
      {ended && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-ks-purple bg-ks-purple-4 p-2" data-return-apply>
          <p role="status" className="text-sm text-ks-ink">{returnApplyText(ended)} <span className="text-ks-grey-1">({enderName(ended.override.ended_by)}, {stamp(ended.override.ended_at)})</span></p>
          <Button type="button" size="sm" variant="outline" data-nonmutating onClick={() => setEnded(null)}>{C.done}</Button>
        </div>
      )}
      {refusal && <p role="alert" className="text-sm">{refusal}</p>}
      {body}
    </section>
  );
}
